import { useCallback, useMemo, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import config from 'constants/config'
import moment from 'moment'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineTagAltIcon from 'LuxKit/Icons/line/LineTagAltIcon'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import { rem } from 'polished'
import USPModal from './USPModal'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import TextLink from 'LuxKit/TextLink'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Caption from 'LuxKit/Typography/Caption'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import LineAwardIcon from 'LuxKit/Icons/line/LineAwardIcon'
import { useIntl } from 'react-intl'

const USPContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: minmax(min-content, max-content) minmax(min-content, max-content);
    align-items: flex-start;
  }
`

const USPBodyText = styled(Caption)`
  margin-left: ${rem(28)};
`

interface Props {
  offer: App.Offer
  checkIn?: string
  availableForPurchase?: boolean
  onViewPackageOptions?: () => void
}

type ModalTypes = 'bnbl' | 'bestPrice' | 'gifting'

function FlashHotelUSPs(props: Props) {
  const { offer, checkIn, availableForPurchase, onViewPackageOptions } = props
  const intl = useIntl()
  const [modalType, setModalType] = useState<ModalTypes>()
  const [open, setOpen] = useState(false)
  const intlDateFormatter = useIntlDateFormatter()

  const onItemClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const modalType = e.currentTarget.dataset.type as ModalTypes
    setModalType(modalType)
    setOpen(true)
  }, [])

  const onClose = useCallback(() => {
    setOpen(false)
  }, [])

  const hasBNBL = offer.hasBuyNowBookLaterPackages && offer.hasBuyNowBookLater && offer.travelToDate && config.accommodation.hasBnbl
  const isSevenDaysBeforeTravelToDate = moment().add(1, 'w').isBefore(offer.travelToDate)
  const isMemberOnlyOffer = offer.luxPlus.access === 'memberOnly'

  const bookBy = useMemo(() => moment(offer.bookByDate), [offer.bookByDate])
  const travelUntil = useMemo(() => moment(offer.travelToDate), [offer.travelToDate])

  const isWePriceMatchEnabled = useOptimizelyExperiment(OptimizelyExperiments.croWePriceMatchOfferPageTag)

  const bestPriceText = isWePriceMatchEnabled
    ? intl.formatMessage({
        id: 'MRdkDgY2YvcfVWLDrcaV',
        defaultMessage: 'We price match',
        description: 'Unique selling point text for the price match guarantee displayed on offer pages',
      })
    : intl.formatMessage({
        id: 'rqMWz3hxH_e43ITD4cAD',
        defaultMessage: 'Best price guarantee',
        description: 'Text for best price guarantee USP in the pricing or cost breakdown section',
      })

  return (
    <>
      <USPContainer>
        {availableForPurchase && <FlashCancellationPolicies
          checkInDate={checkIn}
          propertyTimezoneOffset={offer?.property?.timezoneOffset}
          cancellationPolicy={offer?.lowestPricePackage?.roomRate?.cancellationPolicy}
          isFlashUSP
        />}
        {!offer.bundledWithFlightsOnly && config.bestPriceGuarantee && !offer.disableBestPriceGuarantee && (
          <TextLink
            size="medium"
            colour="highlight-primary-dark"
            underline={false}
            data-type="bestPrice"
            onClick={onItemClick}
            startIcon={isWePriceMatchEnabled ? <LineTagAltIcon /> : <LineAwardIcon />}
          >
            {bestPriceText}
          </TextLink>
        )}
        {hasBNBL && <div>
          <TextLink
            size="medium"
            colour="highlight-primary-dark"
            underline={false}
            data-type="bnbl"
            data-testid="usp-bnbl"
            onClick={onItemClick}
            startIcon={<LineCalendarIcon />}
          >
            {intl.formatMessage({
              id: 'mglfGD7L6Y2B_A0q-4us',
              defaultMessage: 'Buy now, choose dates later',
              description: 'Text for buy now choose dates later USP in the date selection or calendar picker',
            })}
          </TextLink>
          <USPBodyText colour="neutral-three" variant="medium">
            {intl.formatMessage(
              {
                id: 'COIUdQnhEdI9HSyLDOy0',
                defaultMessage: 'Travel until {date}',
                description: 'Text showing travel until date in the date selection or calendar picker',
              },
              {
                date: intlDateFormatter(travelUntil, INTL_DMY_CASUAL_FORMAT),
              },
            )}
          </USPBodyText>
        </div>}
        {hasBNBL && isSevenDaysBeforeTravelToDate && !isMemberOnlyOffer &&
          <TextLink
            size="medium"
            colour="highlight-primary-dark"
            underline={false}
            data-type="gifting"
            onClick={onItemClick}
            startIcon={<LineGiftIcon />}
          >
            {intl.formatMessage({
              id: 'q-sWbOrCqD3itZ0uPTo_',
              defaultMessage: 'Gifting available',
              description: 'Text for gifting available USP on the gift card purchase or redemption page',
            })}
          </TextLink>}
      </USPContainer>
      <USPModal open={open} onClose={onClose} modalType={modalType} bookBy={bookBy} travelUntil={travelUntil} onViewPackageOptions={onViewPackageOptions} />
    </>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  availableForPurchase: isAvailableForPurchase(state, appState),
}), FlashHotelUSPs)
